import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';
import { eth } from '@/lib/format';
import { ChevronDown } from './icons';

/* ────────────────────────────────────────────────────────────────────────────
   Form controls.

   All of them share one field frame: an eyebrow label, a hairline `elevated`
   box that lights up on focus, and a hint line that turns into the error line
   when something is wrong (so the layout height never jumps).
   ──────────────────────────────────────────────────────────────────────────── */

export type FieldSize = 'sm' | 'md' | 'lg';

const FIELD_SIZES: Record<FieldSize, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-xs',
  md: 'h-10 gap-2 px-3 text-sm',
  lg: 'h-12 gap-2 px-3.5 text-[0.9375rem]',
};

const frame = (error?: string, extra?: string) =>
  cn(
    'flex w-full items-center rounded-lg border bg-elevated transition-all duration-150 ease-swift',
    error
      ? 'border-down ring-1 ring-inset ring-down/45'
      : 'border-line focus-within:border-line-strong focus-within:ring-2 focus-within:ring-inset focus-within:ring-bull/30',
    extra,
  );

const control =
  'min-w-0 flex-1 bg-transparent text-fg outline-none placeholder:text-subtle disabled:cursor-not-allowed disabled:opacity-55';

function Label({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-3xs font-medium uppercase tracking-[0.14em] text-subtle"
    >
      {children}
    </label>
  );
}

function Footnote({ hint, error, id }: { hint?: ReactNode; error?: string; id: string }) {
  if (!hint && !error) return null;
  return (
    <p
      id={id}
      role={error ? 'alert' : undefined}
      className={cn('text-2xs leading-snug', error ? 'text-down' : 'text-muted')}
    >
      {error || hint}
    </p>
  );
}

/* ── Input ────────────────────────────────────────────────────────────────── */

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  size?: FieldSize;
  /** Frame class overrides; `className` still targets the `<input>` itself. */
  frameClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, prefix, suffix, size = 'md', className, frameClassName, id, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const noteId = `${inputId}-note`;

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      <div className={frame(error, cn(FIELD_SIZES[size], frameClassName))}>
        {prefix ? (
          <span className="inline-flex shrink-0 items-center text-subtle">{prefix}</span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || error ? noteId : undefined}
          className={cn(control, className)}
          {...rest}
        />
        {suffix ? (
          <span className="inline-flex shrink-0 items-center text-subtle">{suffix}</span>
        ) : null}
      </div>
      <Footnote hint={hint} error={error} id={noteId} />
    </div>
  );
});

/* ── Textarea ─────────────────────────────────────────────────────────────── */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, rows = 4, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const noteId = `${inputId}-note`;

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      <div className={frame(error, 'items-stretch p-3 text-sm')}>
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || error ? noteId : undefined}
          className={cn(control, 'resize-y leading-relaxed', className)}
          {...rest}
        />
      </div>
      <Footnote hint={hint} error={error} id={noteId} />
    </div>
  );
});

/* ── Select ───────────────────────────────────────────────────────────────── */

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  options: SelectOption[];
  size?: FieldSize;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, size = 'md', className, id, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const noteId = `${inputId}-note`;

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      <div className={frame(error, cn(FIELD_SIZES[size], 'relative'))}>
        <select
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || error ? noteId : undefined}
          className={cn(control, 'cursor-pointer appearance-none pr-6', className)}
          {...rest}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} className="bg-surface text-fg">
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-subtle"
        />
      </div>
      <Footnote hint={hint} error={error} id={noteId} />
    </div>
  );
});

/* ── AmountInput ──────────────────────────────────────────────────────────── */

/** Keeps a single decimal point and drops everything that isn't a digit. */
export function sanitizeAmount(raw: string): string {
  let value = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const first = value.indexOf('.');
  if (first !== -1) {
    value = value.slice(0, first + 1) + value.slice(first + 1).replace(/\./g, '');
  }
  if (value.startsWith('.')) value = `0${value}`;
  return value;
}

export interface AmountInputProps {
  value: string;
  onChange: (v: string) => void;
  unit: string;
  balance?: number;
  onMax?: () => void;
  presets?: number[];
  onPreset?: (v: number) => void;
  /** Fiat or token equivalent, shown under the figure. */
  secondary?: ReactNode;
  error?: string;
  autoFocus?: boolean;
  label?: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * The trade panel's headline field: a big monospaced figure on the left, unit
 * and wallet balance on the right, MAX plus preset chips underneath.
 */
export function AmountInput({
  value,
  onChange,
  unit,
  balance,
  onMax,
  presets,
  onPreset,
  secondary,
  error,
  autoFocus,
  label,
  disabled,
  placeholder = '0.0',
  className,
}: AmountInputProps) {
  const inputId = useId();
  const noteId = `${inputId}-note`;
  const hasPresets = Boolean(presets && presets.length > 0);

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      <div
        className={cn(
          'flex w-full flex-col gap-2 rounded-lg border bg-elevated p-3 transition-all duration-150 ease-swift',
          error
            ? 'border-down ring-1 ring-inset ring-down/45'
            : 'border-line focus-within:border-line-strong focus-within:ring-2 focus-within:ring-inset focus-within:ring-bull/30',
        )}
      >
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <input
              id={inputId}
              value={value}
              onChange={(event) => onChange(sanitizeAmount(event.target.value))}
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              autoFocus={autoFocus}
              disabled={disabled}
              placeholder={placeholder}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? noteId : undefined}
              className="w-full min-w-0 bg-transparent font-mono text-2xl font-semibold leading-none tabular-nums text-fg outline-none placeholder:text-subtle disabled:opacity-55"
            />
            {secondary ? (
              <span className="truncate font-mono text-2xs leading-snug text-subtle">
                {secondary}
              </span>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs font-medium uppercase tracking-wide text-muted">
                {unit}
              </span>
              {onMax ? (
                <button
                  type="button"
                  onClick={onMax}
                  disabled={disabled}
                  className="inline-flex min-h-6 items-center rounded border border-bull/35 bg-bull/10 px-2 text-3xs font-semibold uppercase tracking-[0.1em] text-bull transition-colors duration-150 ease-swift hover:bg-bull/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70 disabled:opacity-45"
                >
                  Max
                </button>
              ) : null}
            </div>
            {balance !== undefined ? (
              <span className="whitespace-nowrap font-mono text-2xs tabular-nums text-subtle">
                Bal {eth(balance, { unit: false })}
              </span>
            ) : null}
          </div>
        </div>

        {hasPresets ? (
          <div className="flex flex-wrap items-center gap-1 border-t border-line pt-2">
            {presets?.map((preset) => (
              <button
                key={preset}
                type="button"
                disabled={disabled}
                onClick={() => (onPreset ? onPreset(preset) : onChange(String(preset)))}
                className="inline-flex min-h-6 items-center rounded border border-line bg-surface px-2 font-mono text-2xs tabular-nums text-muted transition-all duration-150 ease-swift hover:border-line-strong hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70 disabled:opacity-45"
              >
                {preset} {unit}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {error ? (
        <p id={noteId} role="alert" className="text-2xs leading-snug text-down">
          {error}
        </p>
      ) : null}
    </div>
  );
}
